import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Car, CalendarDays, Coins, ChevronRight, Power, Image as ImageIcon, ArrowLeft, Star, Upload as UploadIcon, KeyRound, Clock, Camera, Gauge } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatZAR, formatApiErrorDetail, API, formatRate } from "../lib/api";
import { useAuth } from "../lib/auth";
import BulkUploadModal from "../components/BulkUploadModal";
import FleetAnalytics from "../components/FleetAnalytics";
import PayoutPanel from "../components/PayoutPanel";
import { ClaimForm, usePhotoUploads, emptyClaim, claimPayload } from "../components/DamageClaimCard";
import LocationPicker from "../components/LocationPicker";
import { vehiclePhoto } from "../lib/photos";

const TYPES = ["compact", "sedan", "suv", "bakkie", "hatchback", "convertible", "luxury"];
const FUELS = ["petrol", "diesel", "hybrid", "electric"];
const TRANS = ["automatic", "manual"];

export default function OwnerDashboard() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("vehicles");
  const [showWizard, setShowWizard] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [returning, setReturning] = useState(null);

  useEffect(() => {
    if (ready && (!user || user === false)) navigate("/login?next=/owner");
    if (ready && user?.id && user.role !== "owner" && user.role !== "admin") navigate("/dashboard");
  }, [user, ready, navigate]);

  const refresh = () => {
    api.get("/owner/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
    api.get("/owner/bookings").then((r) => setBookings(r.data || [])).catch(() => {});
    api.get("/owner/earnings").then((r) => setEarnings(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.id) refresh(); }, [user?.id]);

  if (!user?.id) return null;

  const activeCount = vehicles.filter((v) => v.is_active).length;

  return (
    <div className="relative min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Host dashboard</p>
            <h1 className="font-brand text-3xl sm:text-4xl text-[#0a1f4d] mt-2">Welcome, {(user.name || "host").split(" ")[0]}</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowBulk(true)} className="btn-outline !py-2.5 text-sm" data-testid="owner-bulk-btn">
              <UploadIcon size={14} /> Bulk import
            </button>
            <button onClick={() => setShowWizard(true)} className="btn-primary text-sm" data-testid="owner-add-vehicle-btn">
              <Plus size={16} /> List a new car
            </button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Active listings" value={activeCount} icon={<Car size={18} />} />
          <Stat label="Bookings" value={bookings.length} icon={<CalendarDays size={18} />} />
          <Stat label="Confirmed bookings" value={bookings.filter((b) => b.status === "confirmed").length} icon={<Star size={18} />} />
          <Stat label="Net earnings" value={formatZAR(earnings?.net_zar || 0)} icon={<Coins size={18} />} />
        </div>

        <div className="mt-8 border-b border-[#E2E8F0] flex gap-6">
          {[
            { key: "vehicles", label: `My cars (${vehicles.length})` },
            { key: "bookings", label: `Bookings (${bookings.length})` },
            { key: "earnings", label: "Earnings & payouts" },
            { key: "analytics", label: "Fleet analytics" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`pb-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-[#1d7ffd] text-[#0a1f4d]" : "border-transparent text-[#64748B] hover:text-[#0a1f4d]"
              }`}
              data-testid={`owner-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "vehicles" && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {vehicles.map((v) => (
              <VehicleAdminCard key={v.id} v={v} onChanged={refresh} />
            ))}
            {vehicles.length === 0 && (
              <div className="col-span-full border border-[#E2E8F0] rounded-2xl p-10 text-center bg-white/70 backdrop-blur">
                <p className="font-display font-bold text-[#0a1f4d] text-lg">No listings yet</p>
                <p className="text-sm text-[#64748B] mt-1">Add your first car to start earning.</p>
                <button onClick={() => setShowWizard(true)} className="btn-primary text-sm mt-5"><Plus size={16}/> List a car</button>
              </div>
            )}
          </div>
        )}

        {tab === "bookings" && (
          <div className="mt-6 space-y-3">
            {bookings.length === 0 && (
              <div className="border border-[#E2E8F0] rounded-2xl p-10 text-center bg-white/70 backdrop-blur">
                <p className="font-display font-bold text-[#0a1f4d]">No bookings yet</p>
                <p className="text-sm text-[#64748B] mt-1">When customers book your cars, they’ll appear here.</p>
              </div>
            )}
            {bookings.map((b) => (
              <div key={b.id} className="flex items-center gap-4 p-4 rounded-2xl border border-[#E2E8F0] bg-white" data-testid={`owner-booking-${b.id}`}>
                <div className="w-20 sm:w-28 aspect-[4/3] rounded-xl overflow-hidden bg-[#F0F2F5] flex-shrink-0">
                  {b.vehicle_photo ? <img src={b.vehicle_photo} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#64748B]"><Car size={20}/></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-display font-bold text-[#0a1f4d] truncate">{b.vehicle_title}</p>
                    <StatusPill status={b.status} />
                  </div>
                  <p className="text-xs text-[#64748B] mt-1">
                    {b.customer_name} · {format(new Date(b.start_date), "d MMM")} – {format(new Date(b.end_date), "d MMM yyyy")}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B]">Earnings</p>
                  <p className="font-display font-black text-[#0a1f4d]">{formatZAR((b.pricing?.rental || 0) + (b.late_fee || 0) + (b.excess_km_fee || 0))}</p>
                  {b.late_fee > 0 && <p className="text-[10px] text-[#B45309]">incl. {formatZAR(b.late_fee)} late fee</p>}
                  {b.excess_km_fee > 0 && <p className="text-[10px] text-[#B45309]">incl. {formatZAR(b.excess_km_fee)} excess km ({b.excess_km} km)</p>}
                </div>
                {b.status === "confirmed" && new Date(b.start_date) <= new Date() && (
                  <button onClick={() => setReturning(b)} className="btn-primary text-xs !py-2 !px-3 whitespace-nowrap" data-testid={`mark-returned-${b.id}`}>
                    <KeyRound size={12} /> Mark returned
                  </button>
                )}
                {b.status === "completed" && (
                  <span className="text-[10px] text-[#065F46] bg-[#10B981]/10 rounded-md px-2 py-1 whitespace-nowrap" data-testid={`deposit-released-${b.id}`}>Deposit released</span>
                )}
                <Link to={`/bookings/${b.id}`} className="text-xs font-semibold text-[#1d7ffd] hover:underline whitespace-nowrap inline-flex items-center gap-1" data-testid={`owner-booking-detail-${b.id}`}><Camera size={12} /> Details & photos</Link>
              </div>
            ))}
          </div>
        )}

        {returning && <ReturnModal booking={returning} onClose={() => setReturning(null)} onDone={() => { setReturning(null); refresh(); }} />}

        {tab === "analytics" && <FleetAnalytics onChanged={refresh} />}

        {tab === "earnings" && earnings && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Stat large label={earnings.late_fees_zar > 0 ? `Gross (incl. ${formatZAR(earnings.late_fees_zar)} late fees)` : "Gross"} value={formatZAR(earnings.gross_zar)} />
            <Stat large label="Service fees" value={`-${formatZAR(earnings.service_fees_zar)}`} />
            <Stat large label="Net payout" value={formatZAR(earnings.net_zar)} accent />
            <div className="lg:col-span-3 rounded-2xl border border-[#E2E8F0] p-6 bg-white">
              <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Monthly earnings</p>
              {earnings.by_month.length === 0 ? (
                <p className="mt-4 text-sm text-[#64748B]">No confirmed bookings yet — your monthly breakdown will appear once your first trip starts.</p>
              ) : (
                <div className="mt-4 space-y-2">
                  {earnings.by_month.map((m) => (
                    <div key={m.month} className="flex items-center gap-3">
                      <span className="text-sm font-semibold text-[#0a1f4d] w-20">{m.month}</span>
                      <div className="flex-1 h-2 rounded-full bg-[#F0F2F5] overflow-hidden">
                        <div className="h-full bg-[#1d7ffd]" style={{ width: `${Math.min(100, (m.amount / Math.max(...earnings.by_month.map((x) => x.amount), 1)) * 100)}%` }} />
                      </div>
                      <span className="text-sm font-bold text-[#0a1f4d] w-24 text-right">{formatZAR(m.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="lg:col-span-3"><PayoutPanel /></div>
          </div>
        )}
      </div>

      {showWizard && <VehicleWizard onClose={() => setShowWizard(false)} onCreated={() => { setShowWizard(false); refresh(); toast.success("Vehicle listed"); }} />}
      {showBulk && <BulkUploadModal onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); refresh(); }} />}
    </div>
  );
}

const Stat = ({ label, value, icon, large, accent }) => (
  <div className={`rounded-2xl border border-[#E2E8F0] p-5 bg-white ${large ? "" : ""}`}>
    {icon && <div className="text-[#1d7ffd]">{icon}</div>}
    <p className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#64748B] mt-3">{label}</p>
    <p className={`font-display font-black mt-1 ${large ? "text-3xl" : "text-2xl"} ${accent ? "text-[#1d7ffd]" : "text-[#0a1f4d]"}`}>{value}</p>
  </div>
);

const StatusPill = ({ status }) => {
  const map = {
    confirmed: { bg: "bg-[#10B981]/15", text: "text-[#10B981]", label: "Confirmed" },
    pending_payment: { bg: "bg-[#F59E0B]/15", text: "text-[#B45309]", label: "Pending payment" },
    cancelled: { bg: "bg-[#EF4444]/15", text: "text-[#B91C1C]", label: "Cancelled" },
    completed: { bg: "bg-[#0a1f4d]/10", text: "text-[#0a1f4d]", label: "Completed" },
  };
  const s = map[status] || { bg: "bg-[#F0F2F5]", text: "text-[#0a1f4d]", label: status };
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-md ${s.bg} ${s.text}`}>{s.label}</span>;
};

function ReturnModal({ booking, onClose, onDone }) {
  const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const [returnedAt, setReturnedAt] = useState(toLocalInput(new Date()));
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reportDamage, setReportDamage] = useState(false);
  const uploads = usePhotoUploads();
  const [claim, setClaim] = useState(emptyClaim(uploads));
  const [full, setFull] = useState(null);
  const [odo, setOdo] = useState({ start: "", end: "" });
  const [odoError, setOdoError] = useState(null);
  useEffect(() => {
    api.get(`/bookings/${booking.id}`).then((r) => {
      setFull(r.data);
      // Pre-fill from odometer readings captured on handover photos
      const photos = r.data.handover_photos || [];
      const pick = photos.filter((ph) => ph.phase === "pickup" && ph.odometer_km != null).map((ph) => ph.odometer_km);
      const ret = photos.filter((ph) => ph.phase === "return" && ph.odometer_km != null).map((ph) => ph.odometer_km);
      setOdo({ start: pick.length ? String(Math.min(...pick)) : "", end: ret.length ? String(Math.max(...ret)) : "" });
    }).catch(() => setFull(booking));
  }, [booking]);

  const odoPayload = () => {
    const o = {};
    if (odo.start !== "") o.odometer_start_km = Number(odo.start);
    if (odo.end !== "") o.odometer_end_km = Number(odo.end);
    return o;
  };

  useEffect(() => {
    if (odo.start !== "" && odo.end !== "" && Number(odo.end) < Number(odo.start)) {
      setPreview(null); setOdoError("Return odometer must be higher than the pickup reading");
      return;
    }
    const iso = new Date(returnedAt).toISOString();
    api.post(`/owner/bookings/${booking.id}/return`, { preview: true, returned_at: iso, ...odoPayload() })
      .then((r) => { setPreview(r.data); setOdoError(null); })
      .catch((e) => { setPreview(null); setOdoError(formatApiErrorDetail(e.response?.data?.detail) || null); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id, returnedAt, odo.start, odo.end]);

  const confirm = async () => {
    setBusy(true);
    try {
      const payload = { returned_at: new Date(returnedAt).toISOString(), notes, ...odoPayload() };
      if (reportDamage) payload.damage_claim = claimPayload({ ...claim, uploads });
      const { data } = await api.post(`/owner/bookings/${booking.id}/return`, payload);
      toast.success(reportDamage ? "Returned — damage claim opened and deposit held" : `Returned — ${formatZAR(data.deposit_refund)} deposit released to the guest`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not mark as returned");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0a1f4d]/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl" data-testid="return-modal">
        <div className="px-6 py-4 border-b border-[#E2E8F0]">
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Confirm return</p>
          <h2 className="font-brand text-2xl text-[#0a1f4d]">{booking.vehicle_title}</h2>
          <p className="text-xs text-[#64748B] mt-1">Due back {format(new Date(booking.end_date), "EEE d MMM, HH:mm")} · {booking.customer_name}</p>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Actual return time</span>
            <input type="datetime-local" className="ip mt-1.5" value={returnedAt} onChange={(e) => setReturnedAt(e.target.value)} data-testid="return-time-input" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Odometer at pickup (km)</span>
              <input type="number" inputMode="numeric" min={0} className="ip mt-1.5" value={odo.start} onChange={(e) => setOdo((o) => ({ ...o, start: e.target.value }))} placeholder="e.g. 45 210" data-testid="return-odo-start" />
            </label>
            <label className="block">
              <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Odometer at return (km)</span>
              <input type="number" inputMode="numeric" min={0} className="ip mt-1.5" value={odo.end} onChange={(e) => setOdo((o) => ({ ...o, end: e.target.value }))} placeholder="e.g. 45 830" data-testid="return-odo-end" />
            </label>
          </div>
          <p className="text-[11px] text-[#64748B] -mt-2">Pre-filled from handover photos when a reading was captured. Leave blank to skip the kilometre check.</p>
          {odoError && <p className="text-xs text-[#B91C1C]" data-testid="return-odo-error">{odoError}</p>}
          <label className="block">
            <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">Notes (optional)</span>
            <input className="ip mt-1.5" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Fuel level, condition…" data-testid="return-notes-input" />
          </label>
          {preview && (
            <div className="rounded-2xl bg-[#F7F8FA] border border-[#E2E8F0] p-4 text-sm space-y-1.5" data-testid="return-preview">
              <div className="flex justify-between"><span className="text-[#64748B]">Deposit held</span><span className="font-semibold text-[#0a1f4d]">{formatZAR(preview.deposit)}</span></div>
              <div className="flex justify-between">
                <span className="text-[#64748B] inline-flex items-center gap-1"><Clock size={12} /> Late fee {preview.late_hours > 0 ? `(${preview.late_hours}h late after ${preview.grace_hours}h grace)` : "(on time)"}</span>
                <span className={`font-semibold ${preview.late_fee > 0 ? "text-[#B45309]" : "text-[#065F46]"}`} data-testid="return-late-fee">-{formatZAR(preview.late_fee)}</span>
              </div>
              {preview.km_driven != null ? (
                <div className="flex justify-between" data-testid="return-km-row">
                  <span className="text-[#64748B] inline-flex items-center gap-1"><Gauge size={12} /> {preview.km_driven.toLocaleString()} km driven · {preview.km_included.toLocaleString()} km included{preview.excess_km > 0 ? ` · ${preview.excess_km.toLocaleString()} km over × ${formatRate(preview.excess_km_rate)}` : ""}</span>
                  <span className={`font-semibold ${preview.excess_km_fee > 0 ? "text-[#B45309]" : "text-[#065F46]"}`} data-testid="return-excess-km-fee">-{formatZAR(preview.excess_km_fee)}</span>
                </div>
              ) : (
                <p className="text-[11px] text-[#64748B]" data-testid="return-km-skipped">No odometer readings — kilometre allowance ({preview.km_included.toLocaleString()} km) not checked.</p>
              )}
              <div className="flex justify-between pt-2 border-t border-[#E2E8F0]"><span className="font-bold text-[#0a1f4d]">Refund to guest</span><span className="font-black text-[#0a1f4d]" data-testid="return-refund">{formatZAR(preview.deposit_refund)}</span></div>
              {preview.outstanding > 0 && <p className="text-xs text-[#B91C1C]" data-testid="return-outstanding">Outstanding from guest: {formatZAR(preview.outstanding)} (charges exceed the deposit)</p>}
              <p className="text-[11px] text-[#64748B] pt-1">Rule: 1h grace, then 1/24 of the daily rate per hour, capped at one day's rate per late day. Excess km are billed at the car's per-km rate.</p>
            </div>
          )}
          <label className="flex items-start gap-3 rounded-2xl border border-[#E2E8F0] p-3 cursor-pointer" data-testid="return-report-damage">
            <input type="checkbox" checked={reportDamage} onChange={(e) => setReportDamage(e.target.checked)} className="mt-1 accent-[#1d7ffd]" data-testid="return-report-damage-checkbox" />
            <span>
              <span className="text-sm font-bold text-[#0a1f4d]">Report damage</span>
              <span className="block text-xs text-[#64748B]">Holds the deposit and opens a claim for tadada to settle. The guest gets 48h to respond.</span>
            </span>
          </label>
          {reportDamage && full && <ClaimForm booking={full} value={{ ...claim, uploads }} onChange={setClaim} compact />}
        </div>
        <div className="px-6 py-4 border-t border-[#E2E8F0] flex justify-end gap-3">
          <button onClick={onClose} className="btn-outline !py-2.5 text-sm" data-testid="return-cancel">Cancel</button>
          <button onClick={confirm} disabled={busy || !preview || (reportDamage && (!claim.amount_claimed || claim.description.length < 10))} className="btn-primary text-sm disabled:opacity-60" data-testid="return-confirm">{busy ? "Saving…" : reportDamage ? "Confirm return & open claim" : "Confirm return & release deposit"}</button>
        </div>
        <style>{`.ip{width:100%;background:#F7F8FA;border:1px solid #E2E8F0;border-radius:0.75rem;padding:10px 14px;font-size:14px;color:#0a1f4d;outline:none}.ip:focus{border-color:#1d7ffd}`}</style>
      </div>
    </div>
  );
}

function VehicleAdminCard({ v, onChanged }) {
  const toggleActive = async () => {
    try {
      await api.patch(`/vehicles/${v.id}`, { is_active: !v.is_active });
      onChanged();
      toast.success(v.is_active ? "Listing paused" : "Listing live");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    }
  };
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden" data-testid={`owner-vehicle-${v.id}`}>
      <div className="aspect-[4/3] bg-[#F0F2F5] relative">
        <img src={vehiclePhoto(v)} alt={v.title} className="w-full h-full object-cover" />
        <span className={`absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md ${v.is_active ? "bg-[#10B981]/20 text-[#065F46]" : "bg-[#64748B]/15 text-[#475569]"}`}>
          {v.is_active ? "Live" : "Paused"}
        </span>
      </div>
      <div className="p-4">
        <p className="font-display font-bold text-[#0a1f4d] truncate">{v.brand} {v.model}</p>
        <p className="text-xs text-[#64748B] mt-1">{v.location_text}, {v.city}</p>
        <p className="font-display text-lg font-black text-[#0a1f4d] mt-2">{formatZAR(v.price_per_day)}<span className="text-xs font-semibold text-[#64748B]">/day</span></p>
        <div className="mt-3 flex gap-2">
          <button onClick={toggleActive} className="flex-1 text-xs font-semibold px-3 py-2 rounded-full border border-[#E2E8F0] text-[#0a1f4d] hover:bg-[#F7F8FA] inline-flex items-center justify-center gap-1" data-testid={`toggle-active-${v.id}`}>
            <Power size={12} /> {v.is_active ? "Pause" : "Activate"}
          </button>
          <Link to={`/vehicles/${v.id}`} className="flex-1 text-xs font-semibold px-3 py-2 rounded-full bg-[#0a1f4d] text-white inline-flex items-center justify-center gap-1 hover:bg-[#0a1f4d]/90">
            View <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function VehicleWizard({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "", brand: "", model: "", year: 2024,
    vehicle_type: "sedan", transmission: "automatic", fuel_type: "petrol", seats: 5,
    city: "Cape Town", location_text: "",
    price_per_day: 600, price_per_hour: 0, price_per_week: 0, price_per_month: 0,
    description: "", features: "",
    delivery_available: false, delivery_fee: 0,
    driver_available: false, driver_fee_per_day: 0,
    insurance_included: true, instant_book: true,
    km_per_day: 200, excess_km_rate: 3.5,
  });
  const [photoUrls, setPhotoUrls] = useState([]);
  const [pin, setPin] = useState(null); // [lat, lng]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value }));
  const setBool = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadPhotos = async (files) => {
    setUploading(true);
    const urls = [...photoUrls];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(`${API}${data.url}`);
      } catch (e) {
        toast.error("Upload failed for one of the files");
      }
    }
    setPhotoUrls(urls);
    setUploading(false);
  };

  const submit = async () => {
    if (!form.title || !form.brand || !form.model || !form.city) {
      toast.error("Please fill in title, brand, model and city");
      return;
    }
    if (photoUrls.length === 0) {
      toast.error("Please upload at least one photo");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/vehicles", {
        ...form,
        photos: photoUrls,
        features: form.features.split(",").map((s) => s.trim()).filter(Boolean),
        lat: pin ? pin[0] : null,
        lng: pin ? pin[1] : null,
      });
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0a1f4d]/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-[#E2E8F0] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">New listing</p>
            <h2 className="font-brand text-2xl text-[#0a1f4d]">List your car</h2>
          </div>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#0a1f4d]" data-testid="wizard-close" aria-label="Close">
            <ArrowLeft size={18} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <Field label="Listing title">
            <input className="ip" required value={form.title} onChange={set("title")} placeholder="e.g. Volkswagen Polo TSI – Compact city ride" data-testid="wiz-title" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand"><input className="ip" value={form.brand} onChange={set("brand")} placeholder="Volkswagen" data-testid="wiz-brand" /></Field>
            <Field label="Model"><input className="ip" value={form.model} onChange={set("model")} placeholder="Polo TSI" data-testid="wiz-model" /></Field>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Year"><input type="number" className="ip" value={form.year} onChange={set("year")} data-testid="wiz-year" /></Field>
            <Field label="Seats"><input type="number" className="ip" value={form.seats} onChange={set("seats")} /></Field>
            <SelectField label="Type" value={form.vehicle_type} onChange={(v) => setForm((f) => ({ ...f, vehicle_type: v }))} opts={TYPES} testid="wiz-type" />
            <SelectField label="Transmission" value={form.transmission} onChange={(v) => setForm((f) => ({ ...f, transmission: v }))} opts={TRANS} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <SelectField label="Fuel" value={form.fuel_type} onChange={(v) => setForm((f) => ({ ...f, fuel_type: v }))} opts={FUELS} />
            <Field label="City"><input className="ip" value={form.city} onChange={set("city")} placeholder="Cape Town" /></Field>
          </div>
          <Field label="Pickup location / neighbourhood"><input className="ip" value={form.location_text} onChange={set("location_text")} placeholder="Sea Point" /></Field>
          <div>
            <p className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B] mb-1.5">Pickup pin on map</p>
            <LocationPicker value={pin} onChange={setPin} city={form.city} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Per day (R)"><input type="number" className="ip" value={form.price_per_day} onChange={set("price_per_day")} data-testid="wiz-price-day" /></Field>
            <Field label="Per hour (R)"><input type="number" className="ip" value={form.price_per_hour} onChange={set("price_per_hour")} /></Field>
            <Field label="Per week (R)"><input type="number" className="ip" value={form.price_per_week} onChange={set("price_per_week")} /></Field>
            <Field label="Per month (R)"><input type="number" className="ip" value={form.price_per_month} onChange={set("price_per_month")} /></Field>
          </div>
          <Field label="Description"><textarea className="ip min-h-[100px]" value={form.description} onChange={set("description")} placeholder="What makes your car special?" /></Field>
          <Field label="Features (comma separated)"><input className="ip" value={form.features} onChange={set("features")} placeholder="Bluetooth, USB-C, Aircon, Apple CarPlay" /></Field>

          <div className="grid grid-cols-2 gap-3">
            <Toggle label="Delivery available" checked={form.delivery_available} onChange={setBool("delivery_available")} />
            {form.delivery_available && <Field label="Delivery fee (R)"><input type="number" className="ip" value={form.delivery_fee} onChange={set("delivery_fee")} /></Field>}
            <Toggle label="Offer chauffeur" checked={form.driver_available} onChange={setBool("driver_available")} />
            {form.driver_available && <Field label="Chauffeur / day (R)"><input type="number" className="ip" value={form.driver_fee_per_day} onChange={set("driver_fee_per_day")} /></Field>}
            <Toggle label="Insurance included" checked={form.insurance_included} onChange={setBool("insurance_included")} />
            <Toggle label="Instant book" checked={form.instant_book} onChange={setBool("instant_book")} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Included km per day"><input type="number" min={50} max={2000} step={10} className="ip" value={form.km_per_day} onChange={set("km_per_day")} data-testid="listing-km-per-day" /></Field>
            <Field label="Excess rate (R per km)"><input type="number" min={0} max={50} step={0.5} className="ip" value={form.excess_km_rate} onChange={set("excess_km_rate")} data-testid="listing-excess-km-rate" /></Field>
          </div>
          <p className="text-xs text-[#64748B] -mt-2">Renters see this as "{form.km_per_day || 0} km/day included · R{Number(form.excess_km_rate || 0).toFixed(2)}/km after". A 3-day trip includes {(Number(form.km_per_day) || 0) * 3} km.</p>

          <div>
            <p className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B] mb-1.5">Photos</p>
            <label className="block border-2 border-dashed border-[#E2E8F0] rounded-2xl p-6 text-center cursor-pointer hover:border-[#1d7ffd] hover:bg-[#1d7ffd]/5 transition-colors">
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(Array.from(e.target.files || []))} data-testid="wiz-photos" />
              <ImageIcon size={20} className="mx-auto text-[#64748B]" />
              <p className="text-sm font-semibold text-[#0a1f4d] mt-2">{uploading ? "Uploading…" : "Click to upload photos"}</p>
              <p className="text-xs text-[#64748B] mt-1">JPG, PNG up to 10 MB each</p>
            </label>
            {photoUrls.length > 0 && (
              <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                {photoUrls.map((u, i) => (
                  <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden border border-[#E2E8F0]">
                    <img src={u} alt={`p${i}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn-outline !py-2.5 text-sm">Cancel</button>
          <button onClick={submit} disabled={submitting} className="btn-primary text-sm disabled:opacity-60" data-testid="wiz-submit">
            {submitting ? "Publishing…" : "Publish listing"}
          </button>
        </div>
        <style>{`.ip{width:100%;background:#F7F8FA;border:1px solid #E2E8F0;border-radius:0.75rem;padding:10px 14px;font-size:14px;color:#0a1f4d;outline:none}.ip:focus{border-color:#1d7ffd}`}</style>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs tracking-[0.14em] uppercase font-bold text-[#64748B]">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const SelectField = ({ label, value, onChange, opts, testid }) => (
  <Field label={label}>
    <select className="ip" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </Field>
);

const Toggle = ({ label, checked, onChange }) => (
  <button type="button" onClick={() => onChange(!checked)} className={`flex items-center justify-between p-3 rounded-xl border ${checked ? "border-[#1d7ffd] bg-[#1d7ffd]/5" : "border-[#E2E8F0] bg-white"}`}>
    <span className="text-sm font-semibold text-[#0a1f4d]">{label}</span>
    <span className={`w-9 h-5 rounded-full relative transition-colors ${checked ? "bg-[#1d7ffd]" : "bg-[#E2E8F0]"}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
    </span>
  </button>
);
